/**
 * The Prism engine manual (`prism docs`), split by topic so an agent reads
 * only what it needs instead of one huge file.
 *
 * A topic is a top-level `# ` section; its `## ` sections are addressable as
 * `topic/section`. Projects get the topics as files in `.prism/docs/` and a
 * short AGENTS.md / CLAUDE.md with the project brief and the topic index; the
 * integrated agent's `prism_docs` tool serves the same index, topics and search.
 */

export interface ManualSection {
  readonly id: string;
  readonly title: string;
  readonly text: string;
}

export interface ManualTopic {
  readonly id: string;
  readonly title: string;
  /** The whole topic, its heading included. */
  readonly text: string;
  /** Text before the first `## ` section. */
  readonly intro: string;
  readonly sections: readonly ManualSection[];
}

/** Where projects keep the topic files, relative to the project root. */
export const MANUAL_DIR = '.prism/docs';

/** Said whenever a change to the manual in a project is refused. */
export const MANUAL_READ_ONLY = `The Prism Engine Cloud manual (${MANUAL_DIR}/) is read-only: Prism Engine Cloud writes it from the engine's own documentation.`;

/**
 * Whether a project-relative path is the engine manual or inside it. The
 * manual is read-only for the editor and every agent: only Prism rewrites it.
 */
export function isManualPath(path: string): boolean {
  const p = normalizeManualPath(path);
  return p === MANUAL_DIR || p.startsWith(`${MANUAL_DIR}/`);
}

/** Whether deleting or moving `path` would change the manual (it, or a folder holding it). */
export function touchesManual(path: string): boolean {
  const p = normalizeManualPath(path);
  return isManualPath(p) || p === '' || p === '.' || `${MANUAL_DIR}/`.startsWith(`${p}/`);
}

function normalizeManualPath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^(\.\/+)+/, '').replace(/\/+$/, '').toLowerCase();
}
/** Most characters one prism_docs answer returns (larger topics are paged). */
export const MANUAL_PAGE_CHARS = 14_000;

/** Line separating the project brief from the manual in AGENTS.md. */
const BRIEF_RULE = /\n---\s*\n/;

export function slug(title: string): string {
  const s = title
    .toLowerCase()
    .replace(/`/g, '')
    .replace(/\(.*?\)/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
  return s || 'section';
}

/** Splits markdown into `# ` topics and their `## ` sections (headings inside code fences do not count). */
export function parseManual(markdown: string): ManualTopic[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const topics: { title: string; lines: string[] }[] = [];
  let fenced = false;
  for (const line of lines) {
    if (/^\s*(```|~~~)/.test(line)) fenced = !fenced;
    if (!fenced && /^# \S/.test(line)) topics.push({ title: line.slice(2).trim(), lines: [line] });
    else if (topics.length) topics[topics.length - 1]!.lines.push(line);
  }
  const used = new Set<string>();
  const unique = (base: string) => {
    let id = base;
    for (let n = 2; used.has(id); n += 1) id = `${base}-${n}`;
    used.add(id);
    return id;
  };
  return topics.map((topic) => {
    const id = unique(slug(topic.title));
    const sections: { title: string; lines: string[] }[] = [];
    const intro: string[] = [];
    let inFence = false;
    for (const line of topic.lines) {
      if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
      if (!inFence && /^## \S/.test(line)) sections.push({ title: line.slice(3).trim(), lines: [line] });
      else if (sections.length) sections[sections.length - 1]!.lines.push(line);
      else intro.push(line);
    }
    const sectionIds = new Set<string>();
    return {
      id,
      title: topic.title,
      text: topic.lines.join('\n').trim(),
      intro: intro.join('\n').trim(),
      sections: sections.map((section) => {
        let sid = `${id}/${slug(section.title)}`;
        for (let n = 2; sectionIds.has(sid); n += 1) sid = `${id}/${slug(section.title)}-${n}`;
        sectionIds.add(sid);
        return { id: sid, title: section.title, text: section.lines.join('\n').trim() };
      }),
    };
  });
}

const kb = (text: string) => `${Math.max(1, Math.round(text.length / 1024))} KB`;

/** One line per topic: id, title, size and the first few section titles. */
export function manualIndex(topics: readonly ManualTopic[], opts: { files?: boolean } = {}): string {
  return topics
    .map((topic) => {
      const subs = topic.sections.map((s) => s.title.replace(/`/g, ''));
      const shown = subs.slice(0, 8).join('; ');
      const more = subs.length > 8 ? `; +${subs.length - 8} more` : '';
      const where = opts.files ? `, \`${MANUAL_DIR}/${topic.id}.md\`` : '';
      return `- \`${topic.id}\`: ${topic.title} (${kb(topic.text)}${where})${shown ? `. Sections: ${shown}${more}` : ''}`;
    })
    .join('\n');
}

function page(text: string, part: number, label: string): string {
  const pages = Math.max(1, Math.ceil(text.length / MANUAL_PAGE_CHARS));
  const index = Math.min(Math.max(1, part), pages);
  if (pages === 1) return text;
  // Cut on line boundaries so tables and code stay readable.
  let start = (index - 1) * MANUAL_PAGE_CHARS;
  let end = Math.min(text.length, index * MANUAL_PAGE_CHARS);
  if (index > 1) start = text.lastIndexOf('\n', start) + 1;
  if (index < pages) end = text.lastIndexOf('\n', end) > start ? text.lastIndexOf('\n', end) : end;
  const body = text.slice(start, end);
  const next = index < pages ? `\n\n[part ${index} of ${pages}; prism_docs topic="${label}" part=${index + 1} for more]` : `\n\n[part ${index} of ${pages}]`;
  return body + next;
}

/** A topic or `topic/section`: small ones whole, large topics as their section list. */
export function readManual(topics: readonly ManualTopic[], id: string, part = 1): string | null {
  const wanted = id.trim().toLowerCase().replace(/\.md$/, '').replace(/^\.prism\/docs\//, '');
  const sectionMatch = topics.flatMap((t) => t.sections).find((s) => s.id === wanted);
  if (sectionMatch) return page(sectionMatch.text, part, sectionMatch.id);
  const topic =
    topics.find((t) => t.id === wanted) ??
    topics.find((t) => t.title.toLowerCase() === wanted) ??
    topics.find((t) => t.id.startsWith(wanted) || t.title.toLowerCase().includes(wanted));
  if (!topic) return null;
  if (topic.text.length <= MANUAL_PAGE_CHARS || part > 1) return page(topic.text, part, topic.id);
  const list = topic.sections.map((s) => `- \`${s.id}\`: ${s.title} (${kb(s.text)})`).join('\n');
  return `${page(topic.intro, 1, topic.id)}\n\n## Sections of this topic (read one with prism_docs topic="<id>")\n${list}`;
}

/** Lines matching every word of the query, grouped by section, best sections first; `part` pages through them. */
export function searchManual(topics: readonly ManualTopic[], query: string, limit = 40, part = 1): string {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);
  if (words.length === 0) return 'Give a query: words that appear in the lines you need (a component, function or file format name).';
  const hits: { id: string; score: number; lines: string[] }[] = [];
  for (const topic of topics) {
    const parts = topic.sections.length ? [{ id: topic.id, title: topic.title, text: topic.intro }, ...topic.sections] : [{ id: topic.id, title: topic.title, text: topic.text }];
    for (const part of parts) {
      const title = part.title.toLowerCase();
      const titleHit = words.every((w) => title.includes(w));
      const lines = part.text.split('\n').filter((line) => {
        const l = line.toLowerCase();
        return words.every((w) => l.includes(w));
      });
      if (!titleHit && lines.length === 0) continue;
      hits.push({ id: part.id, score: (titleHit ? 50 : 0) + lines.length, lines: lines.slice(0, 4).map((l) => l.trim().slice(0, 220)) });
    }
  }
  if (hits.length === 0) return `No manual lines contain all of: ${words.join(', ')}. Try fewer or other words, or read the index (prism_docs with no arguments).`;
  hits.sort((a, b) => b.score - a.score);
  // Pages of about `limit` lines, whole sections each.
  const pages: string[][] = [[]];
  let lines = 0;
  for (const hit of hits) {
    const size = Math.max(1, hit.lines.length);
    if (lines > 0 && lines + size > limit) {
      pages.push([]);
      lines = 0;
    }
    pages[pages.length - 1]!.push(`## ${hit.id} (${hit.score} hit${hit.score === 1 ? '' : 's'})\n${hit.lines.map((l) => `  ${l}`).join('\n')}`);
    lines += size;
  }
  if (part > pages.length)
    return `No more results: the search for "${words.join(' ')}" has ${pages.length} page${pages.length === 1 ? '' : 's'} and you already have ${pages.length === 1 ? 'it' : 'them'}. Read a matching section with prism_docs topic="<id>", or search other words.`;
  const more = part < pages.length ? `\n\n[page ${part} of ${pages.length}; prism_docs query="${query}" part=${part + 1} for more]` : pages.length > 1 ? `\n\n[page ${part} of ${pages.length}, the last]` : '';
  return `${pages[part - 1]!.join('\n\n')}${more}\n\nRead a whole section with prism_docs topic="<id>".`;
}

/** The project brief that leads AGENTS.md (everything before its first `---` rule). */
export function briefOf(agentsMd: string): string {
  const text = agentsMd.replace(/\r\n/g, '\n');
  const match = BRIEF_RULE.exec(text);
  return (match ? text.slice(0, match.index) : '').trim();
}

/** The short AGENTS.md / CLAUDE.md: brief, how to work, and the topic index. */
export function compactAgentsFile(brief: string, topics: readonly ManualTopic[]): string {
  return `${brief.trim()}

---

# Working on this Prism Engine Cloud project

Prism Engine Cloud is a game engine written in Rust (wgpu renderer, ECS, rapier physics, Rhai scripts). Game content is plain text: JSON documents (\`prism.project/1\`, \`prism.scene/1\`, \`prism.prefab/1\`, \`prism.model/1\`, ...) and Rhai gameplay scripts. Y is up, distances are meters, rotations in files are degrees, asset paths are project-relative. Never add three.js or another engine.

- The engine manual is split by topic: one file per topic in \`${MANUAL_DIR}/\` (listed below). Read only the topics you need, or search them for a component, function or format name. The \`prism_docs\` tool serves the same manual: no arguments lists the topics, \`topic\` reads one (or \`topic/section\`), \`query\` searches.
- Start with \`architecture\` (project, scene and prefab formats) for anything about files, and \`prism-script-api-rhai\` for scripting.
- Validate with \`prism_check\`; look at your work with \`prism_screenshot\` / \`prism_model_render\`; test gameplay in Play (\`prism_run_game\`, then \`read_game_trace\` / \`game_state\`).

## Manual topics

${manualIndex(topics, { files: true })}
`;
}

/** True when AGENTS.md still holds the whole manual (projects made before the split). */
export function isMonolithicAgentsFile(text: string): boolean {
  return text.length > 120_000 && /^# Prism Engine/m.test(text);
}
